<!-- src/views/PlanActionPage.vue -->
<template>
  <ion-page>
    <ion-content :fullscreen="true" class="ion-padding">
      <!-- Add the header -->
      <b-slide-header />

      <div class="content-container">
        <div class="header-section">
          <!-- Goal Progress Indicator -->
          <div class="goal-progress">
            <div class="goal-count">Mål {{ currentGoalNumber }} av {{ totalGoals }}</div>
            <div class="progress-bar">
              <div class="progress" :style="{ width: `${(currentGoalNumber / totalGoals) * 100}%` }"></div>
            </div>
          </div>

          <h1 class="main-title">Handlingsplan: {{ goal?.label }}</h1>
          
          <!-- Goal Selection Section - moved to top -->
          <div class="goal-selection-section">
            <div class="goal-info-card">
              <!-- Collapsible header -->
              <button 
                class="goal-header-button"
                @click="goalSelectionOpen = !goalSelectionOpen"
                :class="{ 'goal-saved': goalSelectionSaved }"
              >
                <div class="goal-header-content">
                  <div class="goal-title-section">
                    <h2 class="goal-title">{{ goal?.label }}</h2>
                    <div v-if="goalSelectionSaved" class="checkmark">✓</div>
                  </div>
                  <div class="goal-summary">
                    <p class="goal-description">
                      Jag har valt att öka mitt värde från {{ goal ? getCurrentValue(goal) : 1 }} till {{ goal?.vision || 5 }}.
                      <br>
                      <span class="text-sm text-gray-600 hidden">
                        (alltså från {{ goal ? getValueLabel(getCurrentValue(goal)) : 'Värde 1' }} till {{ goal?.vision ? getValueLabel(goal.vision) : 'Värde 5' }})
                      </span>
                    </p>
                    <p v-if="goalSelectionSaved && goalDate" class="saved-date">
                      Ska klaras senast: {{ goalDate }}
                    </p>
                  </div>
                </div>
                <div class="chevron" :class="{ 'rotated': goalSelectionOpen }">▼</div>
              </button>

              <!-- Collapsible content -->
              <div v-show="goalSelectionOpen" class="goal-content">
                <p class="mb-1 text-base">{{ t('goals.goalAchievedBy') }}</p> 

                <fieldset class="text-[#f87171] border-[#f87171] border-solid rounded">
                  <legend>{{ t('goals.date') }}</legend>
                  <input
                    type="date"
                    class="border-0 w-full text-black max-w-36"
                    v-model="goalDate"
                    :min="minDate"
                  />
                  <p
                    v-if="showDateError"
                    class="mt-4 mb-0 text-red-500 font-medium text-sm"
                  >
                    {{ t('goals.dateRequired') }}
                  </p>
                </fieldset>

                <div class="flex flex-col items-center mt-6">
                  <b-button @click="saveGoalSelection" :disabled="!goalDate">
                    {{ t('goals.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
          
          <p class="main-description">
            Här är det du har RISK på och som påverkar möjligheten att nå detta Mål. Välj ut max 3 faktorer och KLICKA på hur mycket du tänker förbättra varje för att vara säker på att nå Målet {{ goal?.label }}
          </p>
        </div>

        <div v-if="loading" class="loading-indicator">
          <ion-spinner></ion-spinner>
          <p>Laddar faktorer...</p>
        </div>
        <div v-else>

          <!-- Show the connection factors -->
          <template v-if="goal && deduplicatedFactors.length > 0">
            

            <div class="goals-list">
              <div
                v-for="factor in deduplicatedFactors"
                :key="factor.category_id"
                class="goal-section"
                :class="{ 'goal-selected': selected.includes(factor.category_id) }"
              >
                <!-- Factor name -->
                <div class="labels-row flex justify-center items-center flex-1 min-w-0">
                  <div class="bg-transparent !shadow-none label-item">
                    <b class="text-sm font-semibold text-gray-700">{{ factor.label }}</b>
                  </div>
                </div>

                <div class="goal-item">
                  <div class="flex flex-col items-center">
                    <div class="flex space-x-3 text-[18px]">
                      <button
                        v-for="n in 5"
                        :key="n"
                        type="button"
                        :class="[
                          'flex items-center justify-center w-8 h-8 rounded-full text-sm transition cursor-pointer relative',
                          {
                            'bg-red-400 text-white': n === 1,
                            'bg-orange-400 text-white': n === 2,
                            'bg-amber-300 text-white': n === 3,
                            'bg-lime-300 text-white': n === 4,
                            'bg-green-400 text-white': n === 5,
                            'ring-2 ring-offset-2 ring-blue-500 scale-110': 
                              getCurrentValue(factor) === n && 
                              !selected.includes(factor.category_id),
                            'ring-2 ring-offset-2 ring-green-500 scale-110': 
                              selected.includes(factor.category_id) && 
                              visions[factor.category_id] === n
                          }
                        ]"
                        @click="handleClick(factor, n)"
                      >
                        {{ n }}
                        <span v-if="categoryAnswers[factor.category_id]" class="tooltip">
                          Värde {{ n }}: {{ categoryAnswers[factor.category_id][n] }}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selected factors summary -->
            <div v-if="selected.length > 0" class="selected-factors mt-8">
              <h2 class="goals-header mb-4">Områden som du valt för att nå Målet: {{ goal?.label }}</h2>
              <div class="selected-list">
                <div v-for="factorId in selected" :key="factorId" class="selected-factor">
                  <div class="factor-info">
                    <span class="factor-name">{{ getFactorLabel(factorId) }}</span>
                    <div class="factor-values">
                      <span class="current-value">{{ getFactorValue(factorId) }}</span>
                      <span class="arrow">→</span>
                      <span class="target-value">{{ visions[factorId] }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </template>

          <!-- Show message for goals without sambandsfaktorer -->
          <template v-else-if="goal && deduplicatedFactors.length === 0">
            <div class="no-factors-message">
              <p class="text-base text-gray-600 text-center py-8">
                Detta mål har inga förbättringsområden att välja från. Du kan gå direkt till att skriva din handlingsplan.
              </p>
            </div>
          </template>

          <!-- Action plan input -->
          <div v-if="goal" class="action-plan-section mt-8">
            <h2 class="goals-header mb-4">Min handlingsplan för: {{ goal?.label }} </h2>
            <p class="goals-description">Nu, slutligen, ska du bara med egna ord skriva ner vad du kommer att göra för att nå ditt mål: {{ goal?.label }} </p>
            <div class="textarea-container">
              <div class="star-effects">
                <div class="star star-1">⭐</div>
                <div class="star star-2">⭐</div>
                <div class="star star-3">⭐</div>
              </div>
              <textarea
                v-model="planText"
                class="plan-textarea"
                placeholder="Skriv din plan här (max 300 tecken)..."
                maxlength="300"
                @focus="startStarAnimation"
                @input="onTextInput"
              ></textarea>
              <div class="character-count">{{ planText.length }}/300</div>
            </div>

            <div class="navigation-buttons">
              <b-button @click="back">Tillbaka</b-button>
              <b-button @click="handleContinueClick" :disabled="saving">Fortsätt</b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation Modal -->
      <div v-if="showValidationModal" class="validation-modal-overlay" @click="showValidationModal = false">
        <div class="validation-modal" @click.stop>
          <div class="validation-icon">⚠️</div>
          <p class="validation-message">{{ validationError }}</p>
          <button class="validation-close-btn" @click="showValidationModal = false">OK</button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from '@/stores/main';
import { useI18n } from 'vue-i18n';
import { IonPage, IonContent, IonSpinner } from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import Globe from '@/components/illustrations/Globe.vue';
import BButton from '@/components/BButton.vue';

interface Factor {
  category_id: number;
  label: string;
  value: number;
  vision: number;
  affected_by_factors?: Factor[];
  factors?: Factor[];
  [key: string]: any;
}

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

// Provide page data for header
provide('pageData', {
  number: 4,
  title: 'Min livsstilsplan',
  icon: Globe,
});

// Provide color scheme
provide('colorScheme', 'yellow');

// State
const loading = ref(false);
const saving = ref(false);
const goal = ref<Factor | null>(null);
const selected = ref<number[]>([]);
const visions = ref<Record<number, number>>({});
const activeButtons = ref<Record<number, number>>({});
const planText = ref('');
const categoryAnswers = ref<Record<number, Record<string, string>>>({});
const goalDate = ref('');
const goalSelectionOpen = ref(true);
const goalSelectionSaved = ref(false);
const showDateError = ref(false);
const firstRedirectDone = ref(false);
// Use sessionStorage to persist redirect guard across route changes
const redirectKey = 'planActionRedirectDone';
if (sessionStorage.getItem(redirectKey)) {
  firstRedirectDone.value = true;
}

// Progress tracking
const currentGoalNumber = ref(1);
const totalGoals = ref(1);
const isLastGoal = ref(false);

// Computed
const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

// Validation error message
const validationError = ref('');
const showValidationModal = ref(false);

const canContinue = computed(() => {
  // Date is REQUIRED
  const hasDate = !!goalDate.value && goalSelectionSaved.value === true;
  if (!hasDate) return false;
  
  // For goals with factors, user can select 0-3 factors
  // For goals without factors, they can continue
  const hasNoFactors = deduplicatedFactors.value.length === 0;
  return hasNoFactors || true; // Date is the only hard requirement now
});

// Deduplicate factors to avoid showing the same factor twice.
// ONLY use affected_by_factors - these are factors CONNECTED to the goal that are at RISK
// The backend already filters category_connections to only include risk factors
// Prefer entries that have a proper label (not empty and not 'risk').
const deduplicatedFactors = computed(() => {
  if (!goal.value) return [];
  
  // ONLY use affected_by_factors - these come from category_connections 
  // and are already filtered to only include CONNECTED + RISK factors
  // Do NOT include goal.value.factors - those are previously selected factors, not available options
  const allFactors = [...(goal.value.affected_by_factors || [])];
  
  const byId = new Map<number, any>();
  const isGoodLabel = (lbl: any) => {
    const s = (lbl || '').toString().trim().toLowerCase();
    return !!s && s !== 'risk' && !s.startsWith('risk ');
  };
  
  for (const f of allFactors) {
    const id = Number(f?.category_id);
    if (!id) continue;
    
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, f);
    } else {
      // Prefer the one with a better label
      if (!isGoodLabel(existing.label) && isGoodLabel(f.label)) {
        byId.set(id, f);
      }
    }
  }
  return Array.from(byId.values());
});

// Methods
const getCurrentValue = (factor: Factor): number => {
  // First try to get the value from the factor itself
  if (typeof factor.value === 'number') return factor.value;
  if (typeof factor.risk_value === 'number') return factor.risk_value;
  
  // If not available, try to get from store profile
  if (store.profile?.factors?.[factor.category_id]?.value !== undefined) {
    return store.profile.factors[factor.category_id].value;
  }
  
  // Fallback to 1 if nothing is found
  return 1;
};

const handleClick = (factor: Factor, buttonNumber: number) => {
  const originalValue = getCurrentValue(factor);

  // If clicking the original value and a different value is selected, reset to original state
  if (buttonNumber === originalValue && selected.value.includes(factor.category_id)) {
    selected.value = selected.value.filter(id => id !== factor.category_id);
    delete activeButtons.value[factor.category_id];
    delete visions.value[factor.category_id];
    return;
  }

  // Don't allow selecting values lower than original
  if (buttonNumber < originalValue) {
    return;
  }

  // If selecting a new value (different from original)
  if (buttonNumber !== originalValue) {
    // Check max 3 limit before adding
    if (!selected.value.includes(factor.category_id) && selected.value.length >= 3) {
      validationError.value = 'Du kan välja max 3 förbättringsområden per mål.';
      showValidationModal.value = true;
      return;
    }
    
    visions.value[factor.category_id] = buttonNumber;
    if (!selected.value.includes(factor.category_id)) {
      selected.value.push(factor.category_id);
    }
    activeButtons.value[factor.category_id] = buttonNumber;
  }
};

const getValueLabel = (value: number) => {
  if (!goal.value) return `Värde ${value}`;
  const answers = categoryAnswers.value[goal.value.category_id];
  if (answers && answers[String(value)]) {
    return answers[String(value)];
  }
  return `Värde ${value}`;
};

// Helper functions for selected factors summary
const getFactorLabel = (factorId: number): string => {
  const allFactors = [...(goal.value?.factors || []), ...(goal.value?.affected_by_factors || [])];
  const found = allFactors.find(f => f.category_id === factorId)?.label || '';
  if (found && found.toString().trim() && found.toString().trim().toLowerCase() !== 'risk' && !found.toString().trim().toLowerCase().startsWith('risk ')) {
    return found;
  }
  const meta = (store.profile as any)?.factors?.[factorId];
  return (meta?.label || '').toString();
};

const getFactorValue = (factorId: number): number => {
  // Return real risk value so user can see their actual starting point
  const allFactors = [...(goal.value?.factors || []), ...(goal.value?.affected_by_factors || [])];
  const factor = allFactors.find(f => f.category_id === factorId);
  
  if (factor) {
    return getCurrentValue(factor);
  }
  
  // If factor not found in goal data, try to get from store profile
  if (store.profile?.factors?.[factorId]?.value !== undefined) {
    return store.profile.factors[factorId].value;
  }
  
  return 1;
};

const saveGoalSelection = async () => {
  if (!goalDate.value || !goal.value) {
    showDateError.value = true;
    setTimeout(() => {
      showDateError.value = false;
    }, 5000);
    return;
  }

  try {
    console.log('🔄 Saving date to backend for goal:', goal.value.category_id, 'date:', goalDate.value);
    
    // Use the new API endpoint for saving individual goal dates
    await store.saveGoalDate(goal.value.category_id, goalDate.value, []);
    
    goalSelectionSaved.value = true;
    goalSelectionOpen.value = false;
    
    console.log('✅ Goal selection saved with date:', goalDate.value, 'for goal:', goal.value.category_id);
  } catch (error) {
    console.error('❌ Failed to save goal selection:', error);
  }
};

const saveAndContinue = async () => {
  if (!goal.value || saving.value) return;

  try {
    saving.value = true;
    console.log(`🚀 Starting navigation from goal ${goal.value.category_id}`);

    // Create preloader first
    const preloader = document.createElement('div');
    preloader.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #fff;
      opacity: 0;
      transition: opacity 0.3s ease;
      z-index: 9999;
    `;
    document.body.appendChild(preloader);

    try {
      // Show preloader
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          preloader.style.opacity = '1';
          resolve();
        });
      });

      // Always save the goal plan to ensure it's marked as completed in the backend
      // Even if no factors selected and no plan text, we still save to record the goal was processed
      const goalCategoryId = goal.value.category_id;
      const planTextToSave = planText.value;
      const selectedFactors = [...selected.value];
      const visionsToSave = { ...visions.value };
      
      console.log('🚀 About to save goal plan...');
      console.log(`📝 Goal category ID: ${goalCategoryId}`);
      console.log(`📝 Plan text: "${planTextToSave}" (${planTextToSave.length} chars)`);
      console.log(`🎯 Selected factors: [${selectedFactors.join(', ')}]`);
      console.log(`🎯 Visions: ${JSON.stringify(visionsToSave)}`);
      
      const planResult = await store.updateGoalPlan(goalCategoryId, planTextToSave, selectedFactors, visionsToSave);
      console.log('🔄 Plan save result:', planResult);

      if (!planResult || planResult.success === false) {
        console.error('❌ Plan save failed or returned unexpected result:', planResult);
        saving.value = false;
        return;
      }
      
      console.log('✅ Goal plan saved successfully');
      const currentId = goal.value.category_id;
      
      // ALWAYS fetch fresh goal data from backend to ensure we have accurate list
      console.log('🔍 Fetching fresh goal data from backend...');
      const freshGoals = await store.fetchGoals().catch(() => []);
      const freshSelected = (freshGoals || [])
        .filter((g: any) => Number(g.selected) === 1 || String(g.selected) === '1' || g.selected === true)
        .map((g: any) => ({ category_id: Number(g.category_id), label: g.label }));
      
      console.log('📋 Fresh goals from backend:', freshSelected.map((g: any) => g.category_id));
      
      // Build goal list - prefer query params for ordering, but validate against backend
      const querySelectedParam = (route.query?.selected as string | undefined) || '';
      let list: any[] = [];
      
      if (querySelectedParam && freshSelected.length > 0) {
        // Use query param order, but only include goals that exist in backend
        const queryIds = querySelectedParam
          .split(',')
          .map(s => Number(s.trim()))
          .filter(n => !Number.isNaN(n) && n > 0);
        const freshIds = new Set(freshSelected.map((g: any) => g.category_id));
        list = queryIds.filter(id => freshIds.has(id)).map(id => ({ category_id: id }));
        console.log('📋 Using query params (validated):', list.map((g: any) => g.category_id));
      }
      
      // If query params didn't work, use fresh backend data
      if (list.length === 0 && freshSelected.length > 0) {
        list = freshSelected;
        console.log('📋 Using fresh backend data:', list.map((g: any) => g.category_id));
      }
      
      // Final fallback to improvingGoals
      if (list.length === 0 && improvingGoals.value && improvingGoals.value.length > 0) {
        list = improvingGoals.value as any[];
        console.log('📋 Fallback to improvingGoals:', list.map((g: any) => g.category_id));
      }
      
      console.log('📋 Current goal ID:', currentId, 'Type:', typeof currentId);
      console.log('📋 Goal list:', list.map((g: any) => ({ id: g.category_id, type: typeof g.category_id })));

      let currentIndex = list.findIndex((g: any) => Number(g.category_id) === Number(currentId));
      console.log('📋 Current index in list:', currentIndex, 'of', list.length);
      
      // Safety: if current goal not found in list, try to find by string comparison too
      if (currentIndex === -1) {
        currentIndex = list.findIndex((g: any) => String(g.category_id) === String(currentId));
        console.log('📋 Retry with string comparison, index:', currentIndex);
      }
      
      const hasNext = currentIndex >= 0 && currentIndex < list.length - 1;
      console.log('📋 Has next goal:', hasNext);
      const qs = (route.query?.selected as string | undefined) || '';
      const suffix = qs ? `?selected=${encodeURIComponent(qs)}` : '';

      // Determine target URL BEFORE clearing state
      let targetUrl = '/plan/results';
      let nextGoalId: number | null = null;
      
      if (hasNext && list[currentIndex + 1]) {
        nextGoalId = list[currentIndex + 1].category_id;
        targetUrl = `/plan/${nextGoalId}/actions${suffix}`;
        console.log('🚀 Will navigate to next goal:', nextGoalId, 'URL:', targetUrl);
      } else {
        console.log('🏁 No more goals (hasNext:', hasNext, 'listLength:', list.length, 'currentIndex:', currentIndex, ')');
      }

      // SAFETY CHECK: Ensure we're not navigating to the same goal
      const currentPath = window.location.pathname;
      console.log('📍 Current path:', currentPath, 'Target URL:', targetUrl);
      
      if (nextGoalId && Number(nextGoalId) === Number(currentId)) {
        console.error('❌ BUG: Trying to navigate to same goal! Forcing to results page.');
        targetUrl = '/plan/results';
      }
      
      // DEBUG: Show alert if list is unexpectedly small
      if (list.length < totalGoals.value) {
        console.warn(`⚠️ Goal list mismatch! List has ${list.length} items but totalGoals is ${totalGoals.value}`);
        console.warn('List IDs:', list.map((g: any) => g.category_id));
        console.warn('Fresh selected:', freshSelected.map((g: any) => g.category_id));
      }
      
      // If no next goal but we're not on the last goal, something is wrong
      if (!hasNext && currentGoalNumber.value < totalGoals.value) {
        const errorMsg = `Navigation error: Goal ${currentGoalNumber.value} of ${totalGoals.value}, but list only has ${list.length} items. Current ID: ${currentId}, List IDs: ${list.map((g: any) => g.category_id).join(',')}`;
        console.error(errorMsg);
        alert(errorMsg); // Show visible error to user
      }

      // Clear state
      selected.value = [];
      visions.value = {};
      activeButtons.value = {};
      planText.value = '';
      categoryAnswers.value = {};
      goal.value = null;
      
      // Navigate using replace to avoid back button issues
      console.log('🔄 Executing navigation to:', targetUrl);
      window.location.replace(targetUrl);

    } finally {
      // Clean up preloader and reset saving state after delay
      setTimeout(() => {
        saving.value = false; // Reset saving in case navigation failed
        if (preloader && preloader.parentNode) {
          preloader.style.opacity = '0';
          setTimeout(() => preloader.parentNode?.removeChild(preloader), 300);
        }
      }, 2000);
    }
  } catch (error: any) {
    console.error("❌ Failed to process next step:", error);
    saving.value = false;
  }
};

const back = () => {
  router.back();
};

const next = async () => {
  await saveAndContinue();
};

// Handle continue click with validation
const handleContinueClick = () => {
  // Check if date is saved
  if (!goalDate.value || !goalSelectionSaved.value) {
    validationError.value = 'Du måste välja ett datum och spara det innan du kan fortsätta.';
    showValidationModal.value = true;
    return;
  }
  
  // Proceed with save and continue
  next();
};

// Star animation functions
const startStarAnimation = () => {
  const stars = document.querySelectorAll('.star');
  stars.forEach((star, index) => {
    setTimeout(() => {
      star.classList.add('star-eating');
    }, index * 200);
  });
};

const onTextInput = () => {
  // Trigger star animation when user starts typing
  if (planText.value.length === 1) {
    startStarAnimation();
  }
};

// Fetch improving goals to calculate progress
const improvingGoals = ref<Factor[]>([]);

onMounted(async () => {
  // Refresh profile from server to get latest state (handles cross-browser sync)
  const hasActiveProfile = await store.refreshActiveProfile();
  if (!hasActiveProfile) {
    // No active profile - redirect to home
    window.location.href = '/home';
    return;
  }

  const goalId = parseInt(route.params.goalId as string);
  await loadGoalData(goalId);
});

// Watch for route changes to reset state
watch(() => route.params.goalId, async (newGoalId) => {
  if (newGoalId) {
    selected.value = [];
    visions.value = {};
    activeButtons.value = {};
    planText.value = '';
    goalDate.value = '';
    goalSelectionOpen.value = false;
    goalSelectionSaved.value = false;
    await loadGoalData(parseInt(newGoalId as string));
  }
});

async function loadGoalData(goalId: number) {
  loading.value = true;
  try {
    improvingGoals.value = [];
    goal.value = null;

    // Fetch factors for the current goal in parallel with other data to reduce latency
    const [plan, complete, planned, baseGoals, prefetchedFactors] = await Promise.all([
      store.fetchPlan().catch(() => null),
      store.fetchCompletePlan().catch(() => null),
      store.fetchPlannedGoals().catch(() => []),
      store.fetchGoals().catch(() => []),
      store.fetchFactorsForGoal(goalId).catch(() => []),
    ]);

    const lists: any[] = [plan?.goals || [], complete?.goals || [], planned || [], baseGoals || []];
    const union = new Map<number, any>();
    for (const arr of lists) {
      for (const g of arr) {
        const id = Number(g?.category_id);
        if (!id) continue;
        const prev = union.get(id) || {};
        // Merge but preserve non-empty arrays (affected_by_factors, factors)
        const merged = { ...prev, ...g };
        // Don't overwrite non-empty affected_by_factors with empty array
        if (Array.isArray(prev.affected_by_factors) && prev.affected_by_factors.length > 0 && 
            (!Array.isArray(g.affected_by_factors) || g.affected_by_factors.length === 0)) {
          merged.affected_by_factors = prev.affected_by_factors;
        }
        // Don't overwrite non-empty factors with empty array
        if (Array.isArray(prev.factors) && prev.factors.length > 0 && 
            (!Array.isArray(g.factors) || g.factors.length === 0)) {
          merged.factors = prev.factors;
        }
        union.set(id, merged);
      }
    }

    // Enrich with profile factor meta and ensure arrays exist
    union.forEach((g, id) => {
      const meta = (store.profile as any)?.factors?.[id];
      if (!g.label && meta?.label) g.label = meta.label;
      if (g.value == null) {
        if (typeof meta?.value === 'number') g.value = meta.value;
        else if (typeof meta?.risk_value === 'number') g.value = meta.risk_value;
        else g.value = 1;
      }
      if (g.vision == null) g.vision = Math.max(Number(g.value) + 1, 5);
      if (!Array.isArray(g.factors)) g.factors = [];
      if (!Array.isArray(g.affected_by_factors)) g.affected_by_factors = [];
    });

    const baseSelected = (baseGoals || []).filter((g: any) => Number(g.selected) === 1 || String(g.selected) === '1' || g.selected === true).map((g: any) => Number(g.category_id));
    const querySelectedParam = (route.query?.selected as string | undefined) || '';
    const querySelected = querySelectedParam
      ? querySelectedParam.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n))
      : [];
    const selectedSet = new Set<number>([...baseSelected, ...querySelected]);

    // Build a stable order for improving goals: prefer query 'selected' order, else base goals order
    const selectedOrder = (querySelected.length ? querySelected : baseSelected).map(Number);
    const orderMap = new Map<number, number>();
    selectedOrder.forEach((id: number, idx: number) => orderMap.set(Number(id), idx));
    if (orderMap.size === 0) {
      (baseGoals || []).forEach((g: any, idx: number) => orderMap.set(Number(g?.category_id), idx));
    }

    // Ensure placeholders for any selected ids not present in union yet
    selectedSet.forEach((id) => {
      if (!union.has(id)) {
        const meta = (store.profile as any)?.factors?.[id] || {};
        let baseVal = 1;
        if (typeof meta?.value === 'number') baseVal = meta.value;
        else if (typeof meta?.risk_value === 'number') baseVal = meta.risk_value;
        union.set(id, {
          category_id: id,
          label: meta?.label || '',
          value: baseVal,
          vision: Math.max(baseVal + 1, 5),
          selected: 1,
          factors: [],
          affected_by_factors: [],
        });
      }
    });

    const allCombined = Array.from(union.values());

    let improvingList = allCombined.filter((g: any) => selectedSet.has(Number(g.category_id)));
    if (improvingList.length === 0) {
      improvingList = allCombined.filter((g: any) => Number(g.vision) > Number(g.value));
    }
    if (improvingList.length === 0 && allCombined.length > 0) {
      improvingList = allCombined;
    }

    // Sort improving goals by the stable order map (stable sort)
    improvingList.sort((a: any, b: any) => {
      const aid = Number(a.category_id);
      const bid = Number(b.category_id);
      const ai = orderMap.has(aid) ? (orderMap.get(aid) as number) : 9999;
      const bi = orderMap.has(bid) ? (orderMap.get(bid) as number) : 9999;
      if (ai !== bi) return ai - bi;
      // fallback: by label to keep stable
      return String(a.label || '').localeCompare(String(b.label || ''));
    });

    improvingGoals.value = improvingList as any;

    // Ensure current goal is present in list
    const foundIndexInImproving = improvingGoals.value.findIndex((g: any) => Number(g.category_id) === Number(goalId));
    let current = foundIndexInImproving >= 0 ? improvingGoals.value[foundIndexInImproving] : union.get(Number(goalId));
    if (!current && allCombined.length) {
      current = allCombined.find((g: any) => Number(g.category_id) === Number(goalId));
    }
    if (current) {
      // Redirect to first goal ONLY ONCE per session, to ensure "Mål 1 av X"
      const first = improvingGoals.value[0];
      if (!firstRedirectDone.value && first) {
        // Mark redirect as done - either we're already on first goal or we're about to redirect
        firstRedirectDone.value = true;
        sessionStorage.setItem(redirectKey, '1');
        
        // Only redirect if we're NOT already on the first goal
        if (Number(first.category_id) !== Number(goalId)) {
          const qs = (route.query?.selected as string | undefined) || '';
          const suffix = qs ? `?selected=${encodeURIComponent(qs)}` : '';
          window.location.href = `/plan/${first.category_id}/actions${suffix}`;
          return;
        }
      }
      // Build label map from base goals for reliable factor names
      const baseLabelMap = new Map<number, string>();
      (Array.isArray(baseGoals) ? baseGoals : []).forEach((g: any) => {
        const cid = Number(g?.category_id);
        if (cid) baseLabelMap.set(cid, g?.label || '');
      });

      // Normalize current goal label to avoid 'risk'
      const curId = Number((current as any).category_id);
      let glabel = (current as any).label || baseLabelMap.get(curId) || ((store.profile as any)?.factors?.[curId]?.label || '');
      const gnorm = (glabel || '').toString().trim().toLowerCase();
      if (!glabel || gnorm === 'risk' || gnorm.startsWith('risk ')) {
        glabel = baseLabelMap.get(curId) || ((store.profile as any)?.factors?.[curId]?.label || glabel || '');
      }
      current = { ...(current as any), label: glabel } as any;

      // Use prefetched factors (fetched in parallel above) to avoid latency
      // Only fetch again if prefetched data is empty and current goal matches
      if (current.category_id) {
        const factors = (prefetchedFactors && prefetchedFactors.length > 0) 
          ? prefetchedFactors 
          : (current.factors || []);
        current = { ...current, factors, affected_by_factors: current.affected_by_factors || [] };
      }

      const enrich = (arr: any[]) => (arr || []).map((f: any) => {
        const cid = Number(f?.category_id);
        const meta = (store.profile as any)?.factors?.[cid] || {};
        let label = f?.label || baseLabelMap.get(cid) || meta?.label || '';
        const norm = (label || '').toString().trim().toLowerCase();
        if (!label || norm === 'risk' || norm.startsWith('risk ')) {
          label = baseLabelMap.get(cid) || meta?.label || label || '';
        }
        const value = typeof f?.value === 'number'
          ? Number(f.value)
          : (typeof f?.current_value === 'number'
            ? Number(f.current_value)
            : (typeof meta?.value === 'number'
              ? Number(meta.value)
              : (typeof meta?.risk_value === 'number' ? Number(meta.risk_value) : 1)));
        return { ...f, category_id: cid || f?.category_id, label, value };
      });

      const normalizedFactors = enrich(current.factors || []);
      const normalizedAffected = enrich(current.affected_by_factors || []);
      goal.value = { ...current, factors: normalizedFactors, affected_by_factors: normalizedAffected } as any;
      
      // ALWAYS reset date state when loading a goal for editing
      // This forces the user to re-confirm the date before continuing
      goalDate.value = '';
      goalSelectionSaved.value = false;
      goalSelectionOpen.value = true;
      const idx = foundIndexInImproving >= 0 ? foundIndexInImproving : improvingGoals.value.findIndex((g: any) => Number(g.category_id) === Number(goalId));
      currentGoalNumber.value = (idx >= 0 ? idx : 0) + 1;
      totalGoals.value = improvingGoals.value.length;
      isLastGoal.value = idx >= 0 && idx === improvingGoals.value.length - 1;

      if (goal.value && goal.value.category_id) {
        const answers = await store.fetchCategoryAnswers(goal.value.category_id);
        categoryAnswers.value[goal.value.category_id] = answers;
      }
    } else {
      totalGoals.value = improvingGoals.value.length || 1;
      currentGoalNumber.value = 1;
    }
  } catch (error) {
    console.error('Failed to load goal data:', error);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Content Container */
.content-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
}

/* Header Section */
.header-section {
  margin-bottom: 2rem;
}

.goal-progress {
  background-color: #f3f4f6;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.goal-count {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background-color: #10b981;
  transition: width 0.3s ease;
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
}

.main-description {
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
}

/* Loading Indicator */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

/* Goals List */
.goals-list {
  margin-bottom: 2rem;
}

.goal-section {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.goal-section.goal-selected {
  border-color: #10b981;
  background-color: #f0fdf4;
}

.labels-row {
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.label-item {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.goal-item {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Tooltip */
.tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #1f2937;
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: 10;
}

button:hover .tooltip {
  opacity: 1;
}

/* Goal Selection Section */
.goal-selection-section {
  margin-bottom: 2rem;
}

.goal-info-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.goal-header-button {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.goal-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 1.5rem;
}

.goal-title-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.goal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6B3100;
  margin: 0;
}

.goal-summary {
  flex: 1;
  margin-right: 1rem;
}

.goal-description {
  font-size: 1rem;
  color: #333;
  margin: 0.5rem 0;
  line-height: 1.5;
}

.saved-date {
  font-size: 0.9rem;
  color: #059669;
  font-weight: 500;
  margin: 0.5rem 0 0 0;
}

.chevron {
  font-size: 1.2rem;
  color: #6B3100;
  transition: transform 0.3s ease;
  margin-top: 0.5rem;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.goal-content {
  margin-top: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.goal-saved .goal-title {
  color: #059669;
}

/* No Factors Message */
.no-factors-message {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb;
}

/* Action Plan Section */
.action-plan-section {
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.section-description {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.plan-input-container {
  position: relative;
  margin-bottom: 2rem;
}

.plan-textarea {
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.3s ease;
}

.plan-textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.character-count {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
  background-color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Navigation Buttons */
.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .goal-section {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .labels-row {
    justify-content: flex-start;
    margin-bottom: 0.5rem;
  }
  
  .goal-item {
    align-self: center;
    max-width: 100% !important;
    padding: 0 0.5rem;
  }
  
  .goal-item button {
    width: 1.8rem !important;
    height: 1.8rem !important;
    font-size: 0.8rem !important;
  }
  
  .goal-item .flex.space-x-3 {
    gap: 0.25rem !important;
  }
  
  .labels-row b,
  .labels-row span {
    font-size: 0.75rem !important;
  }
  
  .content-container {
    padding: 1rem;
  }
  
  .main-title {
    font-size: 1.5rem;
  }
  
  .main-description {
    font-size: 1rem;
  }
  
  /* Ensure circles container has enough space on mobile */
  .flex.space-x-3 {
    justify-content: center !important;
    flex-wrap: nowrap !important;
    min-width: 100% !important;
  }
  
  /* Make sure the circles don't get cut off */
  .goal-item .flex.flex-col.items-center {
    width: 100% !important;
    overflow: visible !important;
  }
}

/* Goal Selection Section Styles */
.goal-selection-section {
  margin-bottom: 2rem;
}

/* Next Button Section Styles */
.next-button-section {
  background-color: #fff3d2;
  border-radius: 8px;
  padding: 1rem;
  border: 2px solid #fbbf24;
}

/* Collapsible Goal Selection Styles */
.goal-header-button {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.goal-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

/* Mobile layout fix for goal header */
@media (max-width: 768px) {
  .goal-header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .goal-title-section {
    width: 100%;
  }
  
  .chevron {
    align-self: flex-end;
    margin-top: 0;
  }
}

.goal-title-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.goal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6B3100;
  margin: 0;
}

.checkmark {
  background-color: #10b981;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.goal-summary {
  flex: 1;
  margin-right: 1rem;
}

.goal-description {
  font-size: 1rem;
  color: #333;
  margin: 0.5rem 0;
  line-height: 1.5;
}

.saved-date {
  font-size: 0.9rem;
  color: #059669;
  font-weight: 500;
  margin: 0.5rem 0 0 0;
}

.chevron {
  font-size: 1.2rem;
  color: #6B3100;
  transition: transform 0.3s ease;
  margin-top: 0.5rem;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.goal-content {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.goal-saved .goal-title {
  color: #059669;
}

/* Selected factors summary styles */
.selected-factors {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
}

.goals-header {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
}

.selected-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.selected-factor {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 1rem;
}

.factor-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.factor-name {
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

.factor-values {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.current-value {
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
  color: #6b7280;
}

.arrow {
  color: #6b7280;
  font-weight: bold;
}

.target-value {
  background: #dcfce7;
  color: #166534;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

/* Goal selection styles */
.goal-title-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.goal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6B3100;
  margin: 0;
}

.goal-summary {
  flex: 1;
  margin-right: 1rem;
}

.goal-description {
  font-size: 1rem;
  color: #333;
  margin: 0.5rem 0;
  line-height: 1.5;
}

/* Action plan section styles */
.goals-description {
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
}

.textarea-container {
  position: relative;
  width: 100%;
}

.star-effects {
  position: absolute;
  top: -20px;
  right: 10px;
  z-index: 10;
  pointer-events: none;
}

.star {
  display: inline-block;
  font-size: 1.5rem;
  margin: 0 2px;
  opacity: 0;
  transform: scale(0);
  animation: starPop 0.6s ease-out forwards;
}

.star-1 {
  animation-delay: 0s;
}

.star-2 {
  animation-delay: 0.2s;
}

.star-3 {
  animation-delay: 0.4s;
}

@keyframes starPop {
  0% {
    opacity: 0;
    transform: scale(0) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(180deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(360deg);
  }
}

.star.star-eating {
  animation: starEat 0.8s ease-out forwards;
}

@keyframes starEat {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.3) rotate(180deg);
  }
  100% {
    opacity: 0;
    transform: scale(0.5) rotate(360deg);
  }
}

/* Validation Modal */
.validation-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.validation-modal {
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.validation-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.validation-message {
  font-size: 1.1rem;
  color: #374151;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.validation-close-btn {
  background-color: #fdc833;
  color: #6B3100;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.validation-close-btn:hover {
  background-color: #FFDD7D;
}
</style>

